import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import worldTopo from "world-atlas/countries-110m.json";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

const TILE = {
  indigo: "bg-primary/10 text-primary",
  teal: "bg-[hsl(var(--teal))]/12 text-[hsl(var(--teal))]",
  emerald: "bg-emerald-500/12 text-emerald-600 dark:text-emerald-400",
  violet: "bg-violet-500/12 text-violet-600 dark:text-violet-400",
  amber: "bg-amber-500/12 text-amber-600 dark:text-amber-400",
  rose: "bg-rose-500/12 text-rose-600 dark:text-rose-400",
};

export const RANGES = ["1M", "3M", "6M", "12M", "5Y", "10Y"];

export const RangeSelect = ({ value, onChange, testId }) => (
  <div className="flex items-center gap-0.5 rounded-lg border border-border p-0.5" data-testid={testId}>
    {RANGES.map((r) => (
      <button key={r} type="button" onClick={() => onChange(r)}
              data-testid={`${testId}-${r}`}
              className={cn(
                "rounded-md px-1.5 py-0.5 text-[10.5px] font-semibold transition-colors duration-150",
                value === r ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
              )}>
        {r}
      </button>
    ))}
  </div>
);

export const Card = ({ title, action, children, className, testId, padded = true }) => (
  <section className={cn("panel flex h-full flex-col overflow-hidden", className)} data-testid={testId}>
    {(title || action) && (
      <header className="flex items-center justify-between gap-2 px-4 pt-3">
        <h3 className="text-[13.5px] font-semibold text-foreground">{title}</h3>
        {action}
      </header>
    )}
    <div className={cn("min-h-0 flex-1 overflow-auto", padded ? "px-4 pb-4 pt-2" : "")}>{children}</div>
  </section>
);

export const CardLink = ({ children, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId}
          className="inline-flex items-center gap-1 text-[12px] font-medium text-primary transition-opacity duration-150 hover:opacity-75">
    {children} <ArrowUpRight className="h-3 w-3" />
  </button>
);

export const Pill = ({ children, tone = "muted", testId }) => {
  const tones = {
    High: "bg-rose-500/12 text-rose-600 dark:text-rose-300",
    Medium: "bg-amber-500/12 text-amber-600 dark:text-amber-300",
    Low: "bg-emerald-500/12 text-emerald-600 dark:text-emerald-300",
    muted: "bg-muted text-muted-foreground",
    primary: "bg-primary/10 text-primary",
  };
  return (
    <span data-testid={testId}
          className={cn("inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium", tones[tone] || tones.muted)}>
      {children}
    </span>
  );
};

export const Metric = ({ icon: Icon, tone = "indigo", label, value, delta, deltaLabel, footer, testId }) => (
  <div className="panel h-full px-3.5 py-3 transition-shadow duration-200 hover:shadow-[0_8px_24px_-12px_rgba(76,63,196,0.25)]" data-testid={testId}>
    <div className="flex items-center gap-2">
      {Icon && (
        <span className={cn("grid h-6 w-6 place-items-center rounded-md", TILE[tone] || TILE.indigo)}>
          <Icon className="h-3 w-3" />
        </span>
      )}
      <span className="truncate text-[11.5px] font-medium text-muted-foreground">{label}</span>
    </div>
    <div className="mt-1.5 font-display text-[22px] font-bold leading-none tracking-tight text-foreground">
      {value}
    </div>
    <div className="mt-1.5 flex items-center gap-1.5 text-[11px]">
      {typeof delta === "number" && (
        <span className={cn("inline-flex items-center gap-0.5 font-semibold",
          delta >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400")}>
          {delta >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
          {Math.abs(delta)}%
        </span>
      )}
      {footer && <span className="text-muted-foreground">{footer}</span>}
      {deltaLabel && <span className="text-muted-foreground">{deltaLabel}</span>}
    </div>
  </div>
);

const ISO_NAME = {
  IN: "India", GB: "United Kingdom", US: "United States of America", SG: "Singapore",
  KE: "Kenya", AU: "Australia", CA: "Canada", NG: "Nigeria", BD: "Bangladesh", NP: "Nepal",
};

export const WorldReach = ({ reach = [], testId }) => {
  const { t } = useTranslation();
  const active = new Map(reach.map((r) => [ISO_NAME[r.country] || r.country, r.beneficiaries]));
  const max = Math.max(1, ...reach.map((r) => r.beneficiaries));

  return (
    <div className="w-full" data-testid={testId}>
      <div className="h-[168px] w-full overflow-hidden">
        <ComposableMap projection="geoEqualEarth" projectionConfig={{ scale: 168, center: [12, 8] }}
                       width={820} height={370} style={{ width: "100%", height: "100%" }}>
          <Geographies geography={worldTopo}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const value = active.get(geo.properties.name);
                const intensity = value ? 0.25 + (value / max) * 0.65 : 0;
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    style={{
                      default: {
                        fill: value ? `hsl(var(--primary) / ${intensity})` : "hsl(var(--muted-foreground) / 0.16)",
                        stroke: "hsl(var(--card))",
                        strokeWidth: 0.4,
                        outline: "none",
                      },
                      hover: { fill: "hsl(var(--primary) / 0.75)", outline: "none" },
                      pressed: { outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <span className="text-[11px] text-muted-foreground">{t("Reach")}</span>
        <div className="h-1.5 flex-1 rounded-full bg-gradient-to-r from-primary/25 via-primary/60 to-[hsl(var(--teal))]" />
        <span className="text-[11px] text-muted-foreground">0</span>
        <span className="text-[11px] font-medium text-foreground">100K+</span>
      </div>
    </div>
  );
};
